import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo } from "react";
import { Controller, UseFormSetValue, useForm } from "react-hook-form";
import Select, { SingleValue, StylesConfig } from "react-select";

import { TextInput } from "../../Form/TextInput";
import { FormError } from "../FormError";
import {
  CalendarIndexTypes,
  calendarMonths,
  convertDateObjToISO,
  convertISOToDateObj,
  daysInMonth,
  monthsInYear,
  yearOptions
} from "./dateUtils";
import { DatePickerSchema } from "./datepickerSchema";

export const rsStyle: StylesConfig<any, any> = {
  control: (base: any) => ({
    ...base,
    border: "1px solid #ccc !important",
    borderRadius: "0px !important",
    boxShadow: "none !important",
    background: "#fcfdfd !important",
    textTransform: "capitalize"
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#999"
  }),
  placeholder: (defaultStyles: any) => {
    return {
      ...defaultStyles,
      color: "#999",
      fontWeight: 300
    };
  }
};

const now = new Date();
const currYear = now.getFullYear();
const currMonth = now.getMonth();

interface IProps {
  title: string;
  control: any;
  name: string;
  setValue?: UseFormSetValue<any>;
  error?: string;
  value?: string;
  version?: "v1" | "v2";
  theme?: "light" | "dark";
}
type OptionType = { label: string; value: number };

export const DatePicker: FC<IProps> = ({
  title,
  error,
  setValue: setISODate,
  name,
  value,
  version = "v1",
  control: _control,
  theme = "light"
}) => {
  const {
    control,
    formState: { errors, isValid },
    setValue,
    setError,
    watch,
    reset
  } = useForm({
    mode: "all",
    defaultValues: { day: undefined, month: undefined, year: undefined },
    resolver: yupResolver(DatePickerSchema)
  });

  const _dayOptions = daysInMonth(watch("year"), watch("month"));
  const _monthOptions = monthsInYear(watch("year"));
  const _yearOptions = yearOptions();

  useEffect(() => {
    if (isValid) {
      // @ts-ignore
      const iso = convertDateObjToISO(watch());
      setISODate && setISODate(name, iso, { shouldValidate: true });
    }
  }, [isValid]);

  useMemo(() => {
    if (error) {
      if (!watch("day")) setError("day", { type: "custom", message: "Required" });
      if (!watch("month")) setError("month", { type: "custom", message: "Required" });
      if (!watch("year")) setError("year", { type: "custom", message: "Required" });
    }
  }, [error]);

  useMemo(() => {
    if (!!value && isValid === false) {
      // @ts-ignore
      reset(convertISOToDateObj(value));
    }
  }, [value]);

  if (version === "v2") {
    return <TextInput type="date" title={title} control={_control} name={name} id={name} theme={theme} />;
  }

  return (
    <div>
      <p className="text-lg font-semibold mb-2">{title}</p>
      <section className="grid grid-cols-3 gap-x-2">
        <article className="pr-2 mb-5">
          <section>
            <Controller
              name="day"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  onChange={(val: SingleValue<OptionType>) => {
                    onChange((val as OptionType).value);
                  }}
                  className="appearance-none w-full leading-tight focus:outline-none focus:shadow-outline"
                  onBlur={onBlur}
                  value={
                    value
                      ? {
                          label: value,
                          value
                        }
                      : null
                  }
                  name="month"
                  id="month"
                  placeholder="Day"
                  options={_dayOptions}
                  styles={rsStyle}
                />
              )}
            />
          </section>
          <FormError error={errors?.day?.message} />
        </article>
        <article className="px-2 mb-5">
          <section>
            <section>
              <Controller
                name="month"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    onChange={(val: SingleValue<OptionType>) => {
                      onChange(val!.value);
                      const daysInNewMonth = 32 - new Date(watch("year")!, (val as OptionType).value - 1, 32).getDate();

                      if (daysInNewMonth < watch("day")!) {
                        // @ts-ignore
                        setValue("day", daysInNewMonth, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }
                    }}
                    className="appearance-none w-full leading-tight focus:outline-none focus:shadow-outline"
                    onBlur={onBlur}
                    value={
                      value
                        ? {
                            label: calendarMonths[value as CalendarIndexTypes],
                            value
                          }
                        : null
                    }
                    name="month"
                    id="month"
                    placeholder="Month"
                    options={_monthOptions}
                    styles={rsStyle}
                  />
                )}
              />
            </section>
          </section>
          <FormError error={errors?.month?.message} />
        </article>
        <article className="pl-2 mb-5">
          <section>
            <Controller
              name="year"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  onChange={(val: SingleValue<OptionType>) => {
                    onChange((val as OptionType).value);
                    if ((val as OptionType).value === currYear - 12 && currMonth + 1 < watch("month")!) {
                      // @ts-ignore
                      setValue("month", currMonth + 1, {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }
                  }}
                  className="appearance-none w-full leading-tight focus:outline-none focus:shadow-outline"
                  onBlur={onBlur}
                  value={
                    value
                      ? {
                          label: value,
                          value
                        }
                      : null
                  }
                  name="year"
                  id="year"
                  placeholder="Year"
                  options={_yearOptions}
                  styles={rsStyle}
                />
              )}
            />
          </section>
          <FormError error={errors?.year?.message} />
        </article>
      </section>
    </div>
  );
};
